import type { ChipColor } from '@/types/chip';
import type { StudentRecord } from '@/modules/tutoring/shared/types';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Chip, Tooltip, TablePagination, Tabs, Tab, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { VideoLibrary } from '@mui/icons-material';
import { useGetList } from 'react-admin';
import { useState } from 'react';
import { ListPageHeader } from '@/components/ListPageHeader';
import FullCalendarBase from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

interface SessionItem {
  id: string;
  status: string;
  schedule?: { type: string; id: string };
  tutor?: { type: string; id: string };
  students?: Array<{ type: string; id: string }>;
  tutorName?: string;
  studentNames?: string;
  subject?: string;
  startDateTime?: string;
  endDateTime?: string;
}

interface TutorItem {
  id: string;
  legalEntity?: { firstName?: string; lastName?: string };
}

const FullCalendar = FullCalendarBase as unknown as React.ComponentType<Record<string, unknown>>;

const STATUSES = ['Draft', 'Scheduled', 'Confirmed', 'Completed', 'Cancelled', 'Rescheduled'];

const STATUS_COLORS: Record<string, ChipColor> = {
  Draft: 'default',
  Scheduled: 'info',
  Confirmed: 'success',
  Completed: 'secondary',
  Cancelled: 'error',
  Rescheduled: 'warning',
};

const STATUS_BG: Record<string, string> = {
  Draft: '#9e9e9e',
  Scheduled: '#0288d1',
  Confirmed: '#2e7d32',
  Completed: '#7b1fa2',
  Cancelled: '#c62828',
  Rescheduled: '#ed6c02',
};

export const TutoringSessionList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [view, setView] = useState<'calendar' | 'table'>('calendar');
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const { data: allSessions } = useGetList('tutoring-sessions', { pagination: { page: 1, perPage: 1000 }, filter: filters });
  const { data: allTutors } = useGetList('tutors', { pagination: { page: 1, perPage: 1000 } });
  const { data: allStudents } = useGetList('students', { pagination: { page: 1, perPage: 1000 } });

  const sessions = allSessions?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const stats = {
    total: allSessions?.length ?? 0,
    scheduled: allSessions?.filter((s: SessionItem) => s.status === 'Scheduled').length ?? 0,
    completed: allSessions?.filter((s: SessionItem) => s.status === 'Completed').length ?? 0,
    cancelled: allSessions?.filter((s: SessionItem) => s.status === 'Cancelled').length ?? 0,
  };

  const calendarEvents = allSessions?.map((s: SessionItem) => ({
    id: s.id,
    title: `Session${s.startDateTime ? ' — ' + new Date(s.startDateTime).toLocaleDateString() : ''}`,
    start: s.startDateTime,
    end: s.endDateTime,
    backgroundColor: STATUS_BG[s.status] || '#9e9e9e',
    borderColor: STATUS_BG[s.status] || '#9e9e9e',
    extendedProps: { status: s.status },
  }));

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Tutoring Sessions"
        metrics={[
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
          { label: 'Scheduled', value: String(stats.scheduled), color: 'info.main' },
          { label: 'Completed', value: String(stats.completed), color: 'success.main' },
          { label: 'Cancelled', value: String(stats.cancelled), color: 'error.main' },
        ]}
      />

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            label="Status"
            onChange={e => setFilters(prev => ({ ...prev, status: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Tutor</InputLabel>
          <Select
            value={filters.tutorId || ''}
            label="Tutor"
            onChange={e => setFilters(prev => ({ ...prev, tutorId: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {allTutors?.map((t: TutorItem) => <MenuItem key={t.id} value={t.id}>{t.legalEntity?.firstName ?? ""} {t.legalEntity?.lastName ?? ""}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Student</InputLabel>
          <Select
            value={filters.studentId || ''}
            label="Student"
            onChange={e => setFilters(prev => ({ ...prev, studentId: e.target.value || undefined }))}
          >
            <MenuItem value="">All</MenuItem>
            {allStudents?.map((s: StudentRecord) => <MenuItem key={s.id} value={s.id}>{s.legalEntity?.firstName ?? ""} {s.legalEntity?.lastName ?? ""}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Paper sx={{ mt: 2 }}>
        <Tabs
          value={view}
          onChange={(_, v) => setView(v)}
          sx={{ px: 2, pt: 1, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Calendar" value="calendar" />
          <Tab label="Table" value="table" />
        </Tabs>

        {view === 'calendar' && (
          <Box sx={{ p: 2 }}>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay' }}
              events={calendarEvents}
              eventClick={(info: { event: { id: string } }) => navigate(`/tutoring-sessions/${info.event.id}/show`)}
              height="auto"
              contentHeight={650}
              dayMaxEvents={3}
              eventDisplay="block"
            />
          </Box>
        )}

        {view === 'table' && (
          <>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Session</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tutor</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions?.map((session: SessionItem) => (
                  <TableRow
                    key={session.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/tutoring-sessions/${session.id}/show`)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <VideoLibrary sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="body2" fontWeight={500}>{session.id.substring(0, 12) || '—'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {session.studentNames ? (
                        <Tooltip title={session.studentNames}>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {session.studentNames.split(', ').slice(0, 2).map((name, i) => (
                              <Chip key={i} label={name} size="small" variant="outlined" sx={{ maxWidth: 120 }} />
                            ))}
                            {session.studentNames.split(', ').length > 2 && (
                              <Chip label={`+${session.studentNames.split(', ').length - 2}`} size="small" color="primary" variant="outlined" />
                            )}
                          </Box>
                        </Tooltip>
                      ) : '—'}
                    </TableCell>
                    <TableCell><Typography variant="body2">{session.tutorName || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{session.subject || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{session.startDateTime ? new Date(session.startDateTime).toLocaleDateString() : '—'}</Typography></TableCell>
                    <TableCell><Chip label={session.status} size="small" color={STATUS_COLORS[session.status]} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={allSessions?.length ?? 0}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid', borderColor: 'divider' }}
            />
            {(!sessions || sessions.length === 0) && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <VideoLibrary sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                <Typography color="text.secondary">No tutoring sessions found</Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};
